// "Make it my own": a built-in widget turned, in place, into a widget of the
// user's own, with the same look and the same data, written in the widget kit
// so it can be changed freely, by asking or by editing its code. Its settings
// are the constants at the top of its script. The built-in stays in the gallery.

import { canHaveJob } from "../../shared/brief";
import { RETIRED_TYPES, SPEC } from "../../shared/catalog";
import type { WidgetItem, WidgetType } from "../../shared/types";
import { widgetPeriod } from "../../shared/period";
import { commit } from "../state/actions";
import { moreForks } from "./forkKinds";
import { get, set, toast } from "../state/store";

/**
 * The built-in widgets that can be made the user's own: every one but those that read the user's computer, links,
 * pictures and pages, and ContextBolt's own (seo, bookmarks), whose views are drawn from their product's reads: a
 * custom widget the user asks for can read the same stream.
 */
export const FORKABLE: WidgetType[] = ["metric", "chart", "goal", "list", "table", "status", "map", "brief", "feed", "notes", "tasks", "heading", "sticky", "clock", "countdown", "timer"];

export function canFork(w: WidgetItem): boolean {
  // the activity feed reads the board's own log, which a widget of one's own cannot: only a feed of a stream
  return FORKABLE.includes(w.type) && !(w.type === "feed" && w.props?.source !== "stream");
}

// a value written into the script: JSON, with every < escaped, so nothing in it can end the script it sits in
const js = (v: unknown) => JSON.stringify(v).replace(/</g, "\\u003c");
/** one of the values a setting takes, else its default */
const oneOf = <T extends string>(v: unknown, of: readonly T[], d: T): T => (of.includes(v as T) ? (v as T) : d);
const num = (v: unknown, d: number) => (typeof v === "number" && Number.isFinite(v) ? v : d);

/** The widget's code: the same look as the built-in, reading the same stream, its settings written as constants. */
export function forkHtml(w: WidgetItem): string | null {
  const p = w.props ?? {};
  switch (w.type) {
    case "metric": {
      const compare = oneOf(p.compare === "auto" ? "period" : p.compare, ["period", "1d", "7d", "30d", "none"] as const, "period");
      const period = widgetPeriod(w);
      return `<div class="cb-fill" id="main" hidden>
  <div class="cb-split" style="align-items:flex-start">
    <div class="cb-stack" style="--gap:0.15rem;min-width:0">
      <span class="cb-value" id="now"></span>
      <span class="cb-label cb-clip" id="what"></span>
    </div>
    <span class="cb-delta" id="delta" hidden></span>
  </div>
  <div class="cb-chart" id="spark"></div>
</div>
<div class="cb-empty" id="empty"><b>No number yet</b>It shows as soon as its data sends one.</div>
<style>
  [data-size="s"] #spark { display: none; }
</style>
<script>
  // what it compares with: "period" (the period before, or where it was when the period began), "1d", "7d", "30d", or "none"
  var COMPARE = ${js(compare)};
  // the days it shows (0 for all of them); a source read for a period brings its own
  var PERIOD = ${js(period)};
  // a rise is good news ("up") or bad news ("down")
  var GOOD_WHEN = ${js(p.goodWhen === "down" ? "down" : "up")};
  var SPARKLINE = ${js(p.spark !== false)};
  var DAY = 864e5;
  function before(points, ms) {
    for (var i = points.length - 1; i >= 0; i--) if (points[i].t <= ms) return points[i].v;
    return null;
  }
  board.onData(function (d) {
    var has = typeof d.value === "number";
    document.getElementById("main").hidden = !has;
    document.getElementById("empty").hidden = has;
    if (!has) return;
    board.count("#now", d.value);
    document.getElementById("what").textContent = d.label || "";
    var pts = (d.points || []).filter(function (p) { return typeof p.v === "number"; });
    var last = pts.length ? pts[pts.length - 1].t : Date.now();
    if (!d.period && PERIOD) pts = pts.filter(function (p) { return p.t > last - PERIOD * DAY - 1; });
    var days = { "1d": 1, "7d": 7, "30d": 30 }[COMPARE];
    var then = null;
    if (days) then = before(pts, last - days * DAY * 0.94);
    else if (COMPARE === "period") {
      if (d.period) then = typeof d.period.previous === "number" ? d.period.previous : null;
      else {
        then = PERIOD ? before(pts, last - PERIOD * DAY * 0.94) : null;
        if (then === null && pts.length > 1) then = pts[0].v;
      }
    }
    var delta = document.getElementById("delta");
    delta.textContent = then === null ? "" : board.delta(d.value, then);
    var up = then === null || d.value >= then;
    delta.className = "cb-delta " + (up === (GOOD_WHEN === "up") ? "is-good" : "is-bad");
    delta.hidden = !delta.textContent;
    var spark = document.getElementById("spark");
    spark.hidden = !SPARKLINE || pts.length < 2;
    if (!spark.hidden) board.spark("#spark", pts);
  });
</script>`;
    }
    case "chart": {
      const period = widgetPeriod(w);
      const kind = p.chart === "bar" || p.chart === "line" ? p.chart : "area";
      return `<div class="cb-fill" id="main" hidden>
  <div class="cb-stack" id="head" style="--gap:0.1rem">
    <span class="cb-value is-md" id="now"></span>
    <span class="cb-label cb-clip" id="what"></span>
  </div>
  <div class="cb-chart" id="chart"></div>
</div>
<div class="cb-empty" id="empty"><b>No history yet</b>It draws once its data has a few points.</div>
<script>
  // the days it shows, back from its newest point (0 for all of them); a source read for a period brings its own
  var PERIOD = ${js(period)};
  // "area", "line" or "bar"
  var KIND = ${js(kind)};
  var HEADLINE = ${js(p.headline !== false)};
  var AXIS = ${js(p.axis !== false)};
  board.onData(function (d) {
    var pts = (d.points || []).filter(function (p) { return typeof p.v === "number"; });
    var last = pts.length ? pts[pts.length - 1].t : 0;
    if (!d.period && PERIOD) pts = pts.filter(function (p) { return p.t > last - PERIOD * 864e5 - 1; });
    var has = pts.length > 0;
    document.getElementById("main").hidden = !has;
    document.getElementById("empty").hidden = has;
    if (!has) return;
    document.getElementById("head").hidden = !HEADLINE;
    board.count("#now", typeof d.value === "number" ? d.value : pts[pts.length - 1].v);
    document.getElementById("what").textContent = d.label || "";
    if (KIND === "bar") board.bars("#chart", pts, { axis: AXIS });
    else board.line("#chart", pts, { area: KIND === "area", axis: AXIS, highlight: "last" });
  });
</script>`;
    }
    case "goal": {
      const target = num(p.target, 100);
      const start = num(p.start, 0);
      const ring = p.style !== "bar";
      return `<div class="cb-fill" id="main" hidden>
  ${ring ? `<div class="cb-center" style="flex:1;min-height:0"><div id="ring" style="width:100%;height:100%"></div></div>` : `<div class="cb-split" style="align-items:flex-start">
    <div class="cb-stack" style="--gap:0.15rem;min-width:0">
      <span class="cb-value is-md" id="now"></span>
      <span class="cb-label cb-clip" id="of"></span>
    </div>
    <span class="cb-tag is-accent" id="pct"></span>
  </div>
  <div class="cb-bar" id="bar" style="margin:0.46rem 0 0.15rem"></div>`}
  <span class="cb-sub cb-clip" id="eta"></span>
</div>
<div class="cb-empty" id="empty"><b>Nothing to measure yet</b>It fills as soon as its data sends a number.</div>
<script>
  // the target, and where the count started from
  var TARGET = ${js(target)};
  var START = ${js(start)};
  board.onData(function (d) {
    var has = typeof d.value === "number";
    document.getElementById("main").hidden = !has;
    document.getElementById("empty").hidden = has;
    if (!has) return;
    // a total over a period (orders this month) counts from zero, and its days are not a line toward the target
    var total = !!d.period;
    var from = total ? 0 : START;
    var span = TARGET - from || 1;
    var frac = Math.max(0, Math.min(1, (d.value - from) / span));
    var per = !total ? "" : d.period.days === -30 ? " this month" : d.period.days === -90 ? " this quarter" : d.period.days === -365 ? " this year" : d.period.days === 0 ? " of all time" : " in the last " + d.period.days + " days";
    // short of the goal never reads as 100%
    var shown = d.value < TARGET ? Math.min(99, Math.floor(frac * 100)) : 100;
    ${ring ? `board.ring("#ring", frac, { label: board.fmt(d.value) + " of " + board.fmt(TARGET) });` : `board.count("#now", d.value);
    document.getElementById("of").textContent = "of " + board.fmt(TARGET) + (d.label ? " " + d.label : "");
    document.getElementById("pct").textContent = shown + "%";
    document.getElementById("bar").style.setProperty("--pct", (frac * 100).toFixed(1) + "%");`}
    var t = total ? null : board.trend(d.points, { days: 28 });
    var when = t && t.reach(TARGET);
    document.getElementById("eta").textContent = d.value >= TARGET ? "Goal reached" : when ? "At this pace, there by " + new Date(when).toLocaleDateString(undefined, { month: "short", day: "numeric" }) : shown + "% of the way" + per;
  });
</script>`;
    }
    case "list": {
      const max = Math.round(num(p.max, 8));
      return `<div class="cb-fill cb-scroll" id="main" hidden>
  <div class="cb-list" id="rows"></div>
</div>
<div class="cb-empty" id="empty"><b>Nothing here yet</b>It lists what its data sends.</div>
<script>
  // how many rows it shows, and whether each has a bar behind its value and a number in front
  var MAX = ${js(max)};
  var BARS = ${js(p.bars !== false)};
  var NUMBERED = ${js(!!p.numbered)};
  board.onData(function (d) {
    var items = (d.items || []).slice(0, MAX);
    var has = items.length > 0;
    document.getElementById("main").hidden = !has;
    document.getElementById("empty").hidden = has;
    if (!has) return;
    var top = Math.max.apply(null, items.map(function (x) { return typeof x.value === "number" ? x.value : 0; }).concat([0]));
    document.getElementById("rows").innerHTML = items.map(function (x, i) {
      var pct = BARS && top > 0 && typeof x.value === "number" ? Math.max(0, (x.value / top) * 100) : 0;
      return '<div class="cb-item cb-in" style="--i:' + i + ";--pct:" + pct.toFixed(1) + '%">' +
        (NUMBERED ? '<span class="cb-rank">' + (i + 1) + "</span>" : "") +
        '<span class="cb-item-label"><span class="cb-item-title">' + board.esc(x.label || "") + "</span>" +
        (x.sub ? '<span class="cb-item-sub">' + board.esc(x.sub) + "</span>" : "") + "</span>" +
        (x.value !== undefined && x.value !== null ? '<span class="cb-item-value">' + board.esc(board.fmt(x.value)) + "</span>" : "") +
        "</div>";
    }).join("");
  });
</script>`;
    }
    default:
      return moreForks(w);
  }
}

/**
 * Turns a built-in widget into the user's own, where it is: the same title,
 * data, colour, size and place, drawn by code they can change. One undo puts
 * the built-in back.
 */
export function makeOwn(id: string): boolean {
  const it = get().items[id];
  if (!it || it.kind !== "widget" || !canFork(it)) return false;
  const html = forkHtml(it);
  if (!html) return false;
  const was = SPEC[it.type].label;
  // a job it was given goes with it: it fills the same stream
  const job = canHaveJob(it.type) && it.props.auto ? { auto: it.props.auto } : {};
  const next: WidgetItem = { ...it, type: "custom", props: { html, madeFrom: it.type, ...job }, by: null, updatedAt: Date.now() };
  commit([{ op: "item.put", item: next }], "Make it my own");
  set({ selection: [id] });
  toast({ level: "success", title: "It is yours now", body: `“${it.title || was}” is your own widget${it.stream ? ", with the same data" : ""}. Change it by asking, or edit its code. It is in Your widgets in the gallery, to add again${RETIRED_TYPES.has(it.type) ? "" : `, and ${was} is still there too`}.` });
  return true;
}
